import QRCode from "qrcode";
import type { ShareCardModel } from "../shared/invitations.ts";

export function fitCanvasText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  if (context.measureText(text).width <= maxWidth) return text;
  const segments = [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text)].map(({ segment }) => segment);
  const ellipsis = "…";
  while (segments.length && context.measureText(`${segments.join("")}${ellipsis}`).width > maxWidth) segments.pop();
  return `${segments.join("")}${ellipsis}`;
}

export function buildShareData(model: ShareCardModel, file: File): ShareData {
  return { title: model.shareTitle, text: model.shareText, url: model.shareUrl, files: [file] };
}

export async function copyInvitationLink(clipboard: Pick<Clipboard, "writeText">, url: string) {
  try {
    await clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function canvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("名片图片生成失败")), "image/png");
  });
}

export async function createShareCard(model: ShareCardModel) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法生成名片图片");

  context.fillStyle = "#f4f1e8";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#dcece2";
  context.beginPath();
  context.arc(930, 130, 265, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#f4d983";
  context.beginPath();
  context.arc(94, 1220, 220, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = "#fffdf8";
  roundedRect(context, 62, 62, 956, 1226, 54);
  context.fill();
  context.strokeStyle = "#d5ded7";
  context.lineWidth = 3;
  context.stroke();

  context.fillStyle = "#0b6e69";
  roundedRect(context, 116, 116, 90, 90, 26);
  context.fill();
  context.strokeStyle = "#fffdf8";
  context.lineCap = "round";
  context.lineWidth = 10;
  context.beginPath();
  context.arc(161, 159, 22, 0.2, Math.PI * 1.55);
  context.stroke();
  context.fillStyle = "#f4d983";
  context.beginPath();
  context.arc(181, 138, 8, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = "#17332d";
  context.font = '800 54px "Avenir Next Rounded", system-ui, sans-serif';
  context.fillText(model.brand, 232, 179);
  context.fillStyle = "#0b6e69";
  context.font = '800 25px system-ui, sans-serif';
  context.letterSpacing = "2px";
  context.fillText("MOVE GENTLY", 118, 274);

  context.fillStyle = "#17332d";
  context.font = '800 62px "Avenir Next Rounded", system-ui, sans-serif';
  const sloganLines = model.slogan.length > 25
    ? [model.slogan.slice(0, model.slogan.includes("，") ? model.slogan.indexOf("，") + 1 : 20), model.slogan.slice(model.slogan.includes("，") ? model.slogan.indexOf("，") + 1 : 20).trim()]
    : [model.slogan];
  sloganLines.filter(Boolean).forEach((line, index) => context.fillText(line, 118, 370 + index * 82));

  context.fillStyle = "#587069";
  context.font = '600 32px system-ui, sans-serif';
  context.fillText(fitCanvasText(context, model.invitation, 844), 118, sloganLines.length > 1 ? 580 : 500);

  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, model.shareUrl, {
    width: 340,
    margin: 2,
    errorCorrectionLevel: "H",
    color: { dark: "#17332d", light: "#ffffff" },
  });
  context.fillStyle = "#ffffff";
  roundedRect(context, 337, 665, 406, 406, 34);
  context.fill();
  context.drawImage(qrCanvas, 370, 698, 340, 340);

  context.fillStyle = "#587069";
  context.font = '700 24px system-ui, sans-serif';
  context.textAlign = "center";
  context.fillText(model.inviteLabel, 540, 1125);
  context.fillStyle = "#17332d";
  context.font = '800 42px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.letterSpacing = "8px";
  context.fillText(model.inviteCode, 544, 1180);
  context.letterSpacing = "0px";
  context.fillStyle = "#587069";
  context.font = '650 25px system-ui, sans-serif';
  context.fillText("neckmoves.com", 540, 1235);
  context.textAlign = "start";

  return canvasBlob(canvas);
}
